import React, { useState, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Video,
} from 'remotion';
import { Player } from '@remotion/player';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import { CloudUpload } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import parkourVideo from './assets/minecraftparkour.mp4';
import {
  UploadState,
  FeedbackSnackbar,
  ProgressModal,
  callGeminiAPI,
  useIframeResizer,
  uploadToScreenApp,
} from './common';

// Set worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

interface PDFToBrainrotState extends UploadState {
  pdfText: string;
  isLoading: boolean;
  loadingMessage: string;
  showError: boolean;
  fileName: string;
  brainrotContent: string | null;
}

interface Segment {
  startTime: number;
  duration: number;
  text: string;
}

// Remotion component for rendering the brainrot video
const BrainrotComposition: React.FC<{ text: string }> = ({ text }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Parse the timestamped text segments
  const segments = useMemo<Segment[]>(() => {
    return text
      .split('\n')
      .map((line) => {
        const match = line.match(/\[(\d+),\s*(\d+)\]\s*(.*)/);
        if (match) {
          return {
            startTime: parseInt(match[1], 10),
            duration: parseInt(match[2], 10),
            text: match[3],
          };
        }
        return null;
      })
      .filter((segment): segment is Segment => segment !== null);
  }, [text]);

  // Find the current segment based on frame
  const currentTime = frame / fps;
  const currentSegment = segments.find((segment) => {
    const segmentStart = segment.startTime;
    const segmentEnd = segmentStart + segment.duration;
    return currentTime >= segmentStart && currentTime < segmentEnd;
  });

  // Animation properties
  const opacity = interpolate(
    frame - (currentSegment?.startTime ?? 0) * fps,
    [
      0,
      5,
      currentSegment?.duration ? currentSegment.duration * fps - 5 : fps - 5,
      currentSegment?.duration ? currentSegment.duration * fps : fps,
    ],
    [0, 1, 1, 0],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: (t) => {
        // Simple linear fade
        return t;
      },
    }
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: 'black',
        fontFamily: 'Arial',
      }}
    >
      {/* Background Video */}
      <Video
        src={parkourVideo}
        startFrom={0}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        loop
      />

      {/* Overlay for better text readability */}
      <AbsoluteFill
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {currentSegment && (
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
              opacity,
            }}
          >
            <Typography
              variant="h2"
              style={{
                color: 'white',
                textAlign: 'center',
                textShadow: '4px 4px 0px rgba(0,0,0,1)',
                fontFamily:
                  'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif',
                fontWeight: 400,
                maxWidth: '90%',
                wordBreak: 'break-word',
                fontSize: '4rem',
                lineHeight: 1,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                WebkitTextStroke: '2px black',
                padding: '15px',
                background:
                  'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)',
                borderRadius: '15px',
                marginBottom: '10px',
                whiteSpace: 'pre-line',
              }}
            >
              {currentSegment.text}
            </Typography>
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const createBrainrotContent = async (text: string): Promise<string> => {
  console.log('Creating brainrot content from text:', {
    textLength: text.length,
  });

  const prompt = `
Create an addictive, high-energy video script that turns any topic into viral-worthy content. Make it impossible to stop watching, but keep it smart. DO NOT use any Markdown formatting in your response.

1. Hook and grip:
   - Hit hard with an attention-grabbing opener
   - Drop unexpected facts that make viewers double-take
   - Use dramatic pauses and buildups
   - Create "wait, what?" moments
   - Mix in mind-blowing revelations

2. Keep them watching:
   - Build suspense between points
   - Use phrases like "but here's the crazy part" and "this is where it gets wild"
   - Add unexpected twists
   - Create mini-cliffhangers between segments
   - Make each point more interesting than the last

3. Maximum engagement:
   - Keep text punchy and sharp (1-2 lines max)
   - Add shocking comparisons
   - Sprinkle in "trust me, you need to know this"
   - Use power words and strong statements
   - Build up to big reveals

Format each segment with timestamp and duration like this (DO NOT use Markdown):
[0, 3] You won't believe what we just discovered about this
[3, 3] This changes everything we thought we knew
etc.

Keep it around 60 seconds total. Make each segment 2-4 seconds - just enough time for the brain to process before hitting them with the next point.

Source text to convert:
${text}`;

  try {
    return await callGeminiAPI(prompt);
  } catch (error) {
    console.error('Error creating brainrot content:', error);
    throw new Error('Failed to create brainrot content. Please try again.');
  }
};

const PDFToBrainrot: React.FC = () => {
  const { t } = useTranslation();
  useIframeResizer();

  const [state, setState] = useState<PDFToBrainrotState>({
    pdfText: '',
    isLoading: false,
    loadingMessage: '',
    showError: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
    fileName: '',
    brainrotContent: null,
  });

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      console.log('Starting PDF text extraction:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
      setState((prev) => ({ ...prev, loadingMessage: 'Reading PDF file...' }));
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      console.log('PDF loaded successfully:', { numPages: pdf.numPages });
      setState((prev) => ({
        ...prev,
        loadingMessage: `Extracting text from ${pdf.numPages} pages...`,
      }));

      const pagePromises = Array.from(
        { length: pdf.numPages },
        async (_, i) => {
          const pageNum = i + 1;
          try {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            setState((prev) => ({
              ...prev,
              loadingMessage: `Processing page ${pageNum} of ${pdf.numPages}...`,
            }));
            return textContent.items
              .filter((item) => 'str' in item && typeof item.str === 'string')
              .map((item) => (item as { str: string }).str)
              .join(' ');
          } catch (error) {
            console.error(`Error extracting text from page ${pageNum}:`, error);
            return '';
          }
        }
      );

      const textContents = await Promise.all(pagePromises);
      await loadingTask.destroy();

      const fullText = textContents.filter(Boolean).join('\n\n').trim();
      if (!fullText) {
        throw new Error(
          'No readable text found in the PDF. The file might be scanned or image-based.'
        );
      }

      return fullText;
    } catch (error) {
      console.error('PDF extraction error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        'Failed to extract text from PDF. Please make sure the file contains readable text.'
      );
    }
  };

  const handleConvert = async (textToConvert?: string) => {
    const text = textToConvert || state.pdfText;
    if (!text.trim()) {
      console.log('No text to convert');
      return;
    }

    console.log('Starting brainrot content generation');
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      loadingMessage: 'Generating brainrot content...',
      brainrotContent: null,
    }));

    try {
      const brainrotContent = await createBrainrotContent(text);
      console.log('Brainrot content generated successfully:', {
        originalLength: text.length,
        contentLength: brainrotContent.length,
      });

      setState((prev) => ({
        ...prev,
        brainrotContent,
        isLoading: false,
        loadingMessage: '',
      }));
    } catch (err) {
      console.error('Processing failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to process text. Please try again.';
      setState((prev) => ({
        ...prev,
        error: errorMessage,
        showError: true,
        isLoading: false,
        loadingMessage: '',
      }));
    }
  };

  const processFile = async (file: File) => {
    console.log('Processing file:', {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    if (!file.type.includes('pdf')) {
      console.error('Invalid file type:', file.type);
      setState((prev) => ({
        ...prev,
        error: 'Please upload a PDF file',
        showError: true,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      fileName: file.name,
      error: null,
      showError: false,
      pdfText: '',
      loadingMessage: 'Preparing to read PDF...',
      brainrotContent: null,
    }));

    try {
      console.log('Starting text extraction');
      const text = await extractTextFromPDF(file);
      console.log('Text extraction successful:', { textLength: text.length });

      setState((prev) => ({
        ...prev,
        pdfText: text,
        isLoading: true,
        loadingMessage: 'Starting video generation...',
      }));

      await handleConvert(text);
    } catch (error) {
      console.error('File processing failed:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to read PDF file. Please try again with a different PDF.';

      setState((prev) => ({
        ...prev,
        error: errorMessage,
        showError: true,
        isLoading: false,
        pdfText: '',
        loadingMessage: '',
      }));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleDownload = async () => {
    setState((prev) => ({ ...prev, uploading: true, progress: 0 }));

    // Create a blank file as a placeholder
    const blob = new Blob([''], { type: 'application/octet-stream' });

    try {
      await uploadToScreenApp(
        blob,
        'brainrot.mp4',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => setState((prev) => ({ ...prev, uploadComplete: true })),
        (error) =>
          setState((prev) => ({
            ...prev,
            error,
            showError: true,
            uploading: false,
          })),
        true // Open in new tab
      );
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to start download',
        showError: true,
        uploading: false,
      }));
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          p: 3,
          maxWidth: '60rem',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {state.brainrotContent && (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                disabled={state.uploading}
                sx={{
                  minWidth: 160,
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: (theme) =>
                      `0 4px 12px ${theme.palette.primary.main}40`,
                  },
                  transition: 'all 0.2s',
                }}
              >
                {state.uploading ? 'Processing...' : 'Download'}
              </Button>
            </Box>
            <Box
              sx={{
                mb: 3,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  aspectRatio: '9/16',
                  width: 'auto',
                  height: '700px',
                  maxHeight: '700px',
                  position: 'relative',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <Player
                  component={BrainrotComposition}
                  durationInFrames={30 * 60}
                  fps={30}
                  compositionWidth={1080}
                  compositionHeight={1920}
                  style={{
                    width: 'auto',
                    height: '100%',
                  }}
                  controls
                  autoPlay
                  inputProps={{
                    text: state.brainrotContent,
                  }}
                />
              </Box>
            </Box>
          </>
        )}

        {!state.isLoading && !state.uploading && (
          <>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                borderStyle: 'dashed',
                borderWidth: 3,
                borderRadius: '12px',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                borderColor: isDragging ? 'primary.main' : 'secondary.dark',
                transition: 'all 0.2s ease-in-out',
                mb: 2,
                width: '100%',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                },
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf"
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  '@keyframes floatAnimation': {
                    '0%': {
                      transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                    },
                    '50%': {
                      transform: isDragging
                        ? 'scale(1.1) translateY(-5px)'
                        : 'scale(1) translateY(-5px)',
                    },
                    '100%': {
                      transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                    },
                  },
                }}
              >
                <CloudUpload
                  sx={{
                    fontSize: 56,
                    color: isDragging ? 'primary.main' : 'text.secondary',
                    transition: 'all 0.2s ease-in-out',
                    transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                    '.MuiPaper-root:hover &': {
                      animation: 'floatAnimation 2s ease-in-out infinite',
                    },
                  }}
                />

                <Typography variant="h5" fontWeight="medium" sx={{ mb: 1 }}>
                  {state.brainrotContent
                    ? 'Create Another Brainrot'
                    : t('pdfToBrainrot.controls.upload')}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {state.brainrotContent
                    ? 'Upload another PDF'
                    : 'or drop your PDF file here'}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Supports PDF files only
                </Typography>
              </Box>

              {state.error && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {state.error}
                </Typography>
              )}
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 4,
                borderRadius: '12px',
                border: 'none',
                width: '100%',
              }}
            >
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 2, textAlign: 'center' }}
              >
                {state.brainrotContent
                  ? 'Create Another Brainrot'
                  : 'Or enter text directly'}
              </Typography>
              <TextField
                multiline
                rows={6}
                fullWidth
                placeholder="Enter your text here to convert to brainrot..."
                value={state.pdfText}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, pdfText: e.target.value }))
                }
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleConvert()}
                  disabled={!state.pdfText.trim()}
                  sx={{
                    minWidth: 160,
                    margin: '0 auto',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) =>
                        `0 4px 12px ${theme.palette.primary.main}40`,
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  Make Brainrot
                </Button>
              </Box>
            </Paper>
          </>
        )}

        {(state.isLoading || state.uploading) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" align="center">
              {state.loadingMessage}
            </Typography>
          </Box>
        )}

        <ProgressModal
          open={state.uploading}
          progress={state.progress}
          isComplete={state.uploadComplete}
          processingMessage={t('pdfToBrainrot.messages.processing')}
          completeMessage={t('pdfToBrainrot.messages.complete')}
          onClose={() => {
            setState((prev) => ({
              ...prev,
              uploading: false,
              pdfText: '',
              brainrotContent: null,
            }));
          }}
        />

        <FeedbackSnackbar
          open={!!state.error && state.showError}
          message={state.error || t('pdfToBrainrot.messages.error')}
          severity="error"
          onClose={() => setState((prev) => ({ ...prev, showError: false }))}
        />
      </Box>
    </Box>
  );
};

export default PDFToBrainrot;
